import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  MutableRefObject,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AppContext } from '../../../core/context/appContextProvider';
import { updateBankTransactions } from '../../../services/bank.service';
import { getCapitalCallTransactions } from '../../../services/capitalCalls.service';
import { GET_CAPITAL_CALL_TRANSACTIONS_ERROR } from '../../../services/hooks/useCapitalCallsEffect/useCapitalCall.constants';
import { M_DASH_UNICODE } from '../../../utils/constants/constants';
import {
  CurrencyFormat,
  DateTimeFormat,
} from '../../../utils/helpers/format.helper';
import { useEffectAsync } from '../../../utils/hooks/useEffectAsync.hook';
import {
  NameIdPair,
  UpdateBankTransactionsPayload,
} from '../../../utils/types/bank.type';
import { CapitalCalls } from '../../../utils/types/capitalCalls.type';
import { CashReceiptTransaction } from '../../../utils/types/cashReceipt.type';
import { ProgressBar } from '../bankConnectionPanel/bankConnectionPanel.styles';
import { BankStatusOptions } from '../bankFeeds/BankFeedList.defaults';
import {
  BlueAddCircleOutlineOutlinedIcon,
  CellBox,
  TwoStepSelectButtonBox,
} from '../bankFeeds/BankFeedList.styles';
import { EditableCapCallChip, ReadOnlyCapCallChip } from './DataGridChips';

type twoStepSelectType = 'CAP_CALL' | 'TRANSACTION';

type Props = {
  isAdmin: boolean;
  row: any;
  capitalCallList: any[];
  fetchBankTransactions: MutableRefObject<() => void>;
};

export const CapitalCallCellRenderer: React.FC<Props> = ({
  isAdmin,
  row,
  capitalCallList = [],
  fetchBankTransactions,
}: Props): ReactElement => {
  const { informationAlert } = useContext(AppContext);

  const isReadOnly = !isAdmin || row.status === BankStatusOptions.Categorized;

  const [filteredCapCalls, setFilteredCapCalls] = useState<CapitalCalls[]>();
  const [isHovered, setIsHovered] = useState(false);
  const [step, setStep] = useState<twoStepSelectType>('CAP_CALL');
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [selectedCapitalCall, setSelectedCapitalCall] = useState<NameIdPair>();
  const [transactionsResponse, setTransactionsResponse] = useState<
    CashReceiptTransaction[]
  >([]);
  const [transactionList, setTransactionList] = useState<
    CashReceiptTransaction[]
  >([]);
  const [selectedCapCallTransaction, setSelectedCapCallTransaction] =
    useState<CashReceiptTransaction>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (isReadOnly) {
    return row.receivableTransactionId ? <ReadOnlyCapCallChip /> : <></>;
  }

  useEffect(() => {
    const filteredCapCalls = capitalCallList.filter(
      (capCall) => capCall.fundId === row.fundId
    );

    setFilteredCapCalls(filteredCapCalls);
  }, [capitalCallList, row]);

  const fetchCapitalCallTransactions = async (isCanceled?: () => boolean) => {
    if (selectedCapitalCall) {
      try {
        setIsLoading(true);

        const response = await getCapitalCallTransactions(
          selectedCapitalCall.id!!
        );

        if (isCanceled?.()) return;

        setTransactionsResponse(response);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        informationAlert(GET_CAPITAL_CALL_TRANSACTIONS_ERROR, 'error');
      }
    }
  };

  useEffectAsync(
    async (isCanceled) => {
      if (selectedCapitalCall) {
        try {
          await fetchCapitalCallTransactions(isCanceled);

          setStep('TRANSACTION');
        } catch (e) {
          informationAlert('Error getting capital call transactions', 'error');
        }
      }
    },
    [selectedCapitalCall]
  );

  const prepRowsForDataGrid = (transactions: CashReceiptTransaction[]) => {
    if (transactions && transactions.length > 0) {
      const updatedTransactions: any[] = transactions.map((t) => {
        let value = t.amount - t.amountPaid;

        if (t.adjustments && t.adjustments[0] && t.adjustments[0].amountPaid) {
          value = value - t.adjustments[0].amountPaid;
        }

        if (t.adjustments && t.adjustments[1] && t.adjustments[1].amountPaid) {
          value = value - t.adjustments[1].amountPaid;
        }

        return {
          ...t,
          remainingDue: value,
        };
      });

      return updatedTransactions.sort((a, b) =>
        a.remainingDue < b.remainingDue
          ? 1
          : b.remainingDue < a.remainingDue
          ? -1
          : 0
      );
    }
  };

  useEffect(() => {
    if (transactionsResponse && transactionsResponse.length > 0) {
      const processedList = prepRowsForDataGrid(transactionsResponse);

      if (processedList) setTransactionList(processedList);
    }
  }, [transactionsResponse]);

  const handleStartEditing = () => {
    setIsEditing(true);
    setOpenDropdown(true);
  };

  const handleCapitalCallSelection = (capCall: NameIdPair) => {
    if (selectedCapitalCall?.id === capCall.id) {
      setStep('TRANSACTION');
    } else {
      setSelectedCapitalCall(capCall);
    }
  };

  const handleBackClick = () => {
    setStep('CAP_CALL');
  };

  const handleTransactionSelection = async (
    selectedTransaction: CashReceiptTransaction
  ) => {
    setSelectedCapCallTransaction(selectedTransaction);
    setOpenDropdown(false);
    setIsEditing(false);

    const payload: UpdateBankTransactionsPayload = {
      id: row.id,
      status: row.status,
      isPlaid: row.isPlaid,
      receivableTransactionId: selectedTransaction.id,
    };

    try {
      setIsLoading(true);
      await updateBankTransactions([payload]);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      informationAlert('Error assigning capital call transaction', 'error');
    } finally {
      await fetchBankTransactions.current();
    }
  };

  const handleClose = () => {
    setOpenDropdown(false);
    setIsEditing(false);
    setIsHovered(false);
  };

  const handleDelete = async () => {
    const payload: UpdateBankTransactionsPayload = {
      id: row.id,
      status: row.status,
      isPlaid: row.isPlaid,
      receivableTransactionId: null,
    };

    try {
      setIsLoading(true);
      await updateBankTransactions([payload]);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      informationAlert(
        'Error deleting assigned capital call transaction',
        'error'
      );
    } finally {
      await fetchBankTransactions.current();
    }
  };

  return isEditing ? (
    <FormControl size="small" fullWidth onClick={() => {}}>
      <>
        <InputLabel id={`fund_gl_input_label_`} shrink={false}>
          Capital Call
        </InputLabel>
        <Select
          id={`fund_gl_select_`}
          labelId="fund_gl_input_label_"
          fullWidth
          open={openDropdown}
          onOpen={() => setOpenDropdown(true)}
          onClose={() => {}}
          MenuProps={{
            onClose: handleClose,
          }}
        >
          <div>
            {step === 'CAP_CALL' && (
              <>
                <Divider>Select Capital Call</Divider>
                {!filteredCapCalls?.length && (
                  <MenuItem
                    key={'no_cap_calls'}
                    disabled={true}
                    value={'no_cap_calls'}
                  >
                    No Available Capital Calls
                  </MenuItem>
                )}
                {filteredCapCalls?.map((option: any, key) => {
                  return (
                    <MenuItem
                      selected={
                        selectedCapitalCall?.id === option.id ? true : false
                      }
                      onClick={() => handleCapitalCallSelection(option)}
                      key={key}
                      value={option.id}
                    >
                      {isLoading && selectedCapitalCall?.id === option.id && (
                        <ProgressBar />
                      )}
                      {option.label}
                    </MenuItem>
                  );
                })}
              </>
            )}
            {step === 'TRANSACTION' && (
              <>
                <TwoStepSelectButtonBox>
                  <Button
                    id="btn_back_to_cap_calls"
                    variant="text"
                    onClick={handleBackClick}
                    color={'primary'}
                  >
                    <ChevronLeftIcon />
                    Cap Calls
                  </Button>
                </TwoStepSelectButtonBox>

                <Divider>Select Transaction</Divider>

                {transactionList.map((option, key) => {
                  return (
                    <MenuItem
                      selected={
                        selectedCapCallTransaction?.id === option.id
                          ? true
                          : false
                      }
                      onClick={() => handleTransactionSelection(option)}
                      key={key}
                      value={option.id}
                      disabled={option.adjustments.length === 2}
                    >
                      {`${option.investor} - ${DateTimeFormat.shortDateString(
                        option.date as string
                      )} - ${CurrencyFormat(row.currency, 0).format(
                        option.amount
                      )}`}
                    </MenuItem>
                  );
                })}
              </>
            )}
          </div>
        </Select>
      </>
    </FormControl>
  ) : (
    <CellBox
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {row.receivableTransactionId ? (
        <EditableCapCallChip
          onChipClick={handleStartEditing}
          onDelete={handleDelete}
        />
      ) : isHovered ? (
        <Chip
          label={
            filteredCapCalls?.length ? 'Add Capital Call' : 'None Available'
          }
          icon={<BlueAddCircleOutlineOutlinedIcon />}
          onClick={() => handleStartEditing()}
          disabled={!filteredCapCalls?.length}
        />
      ) : (
        M_DASH_UNICODE
      )}
      {isLoading && <ProgressBar />}
    </CellBox>
  );
};
